import { useEffect, useRef } from 'react';
import type { StoryTrackingData } from '../services/tracking/storyEvent';
import { trackStoryEvent } from '../services/tracking/trackClientInfo';

interface ChapterEndTrackingProps {
  storyId: string;
  storyName: string;
  originalStoryId: string;
  chapterNumber: number;
  chapterTitle: string;
  isSummaryScreen: boolean;
  publisher?: StoryTrackingData['publisher'];
  elementRef?: React.RefObject<HTMLElement | null>;
}

export const useChapterEndTracking = ({
  storyId,
  storyName,
  originalStoryId,
  chapterNumber,
  chapterTitle,
  isSummaryScreen,
  publisher,
  elementRef,
}: ChapterEndTrackingProps) => {
  const chapterEndVisibilityTracked = useRef(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !chapterEndVisibilityTracked.current) {
            chapterEndVisibilityTracked.current = true;

            try {
              trackStoryEvent(
                'reader_chapter-end_reached',
                { publisher },
                {
                  slug: storyId,
                  originalStoryId,
                  currentEpisode: chapterNumber.toString(),
                  isSummaryScreen,
                  storyName,
                  chapterTitle,
                },
              );
            } catch (error) {
              trackStoryEvent(
                'reader_chapter-end_tracking-error',
                { publisher },
                {
                  error: error instanceof Error ? error.message : 'Unknown error',
                  slug: storyId,
                  originalStoryId,
                  currentEpisode: chapterNumber.toString(),
                },
              );
            }
          }
        });
      },
      { threshold: 0.1 },
    );

    if (elementRef?.current) {
      observer.observe(elementRef.current);
    }

    return () => observer.disconnect();
  }, [storyId, originalStoryId, chapterNumber, storyName, chapterTitle, isSummaryScreen, publisher, elementRef]);
};
